import { HubConnection, HubConnectionBuilder, HubConnectionState, LogLevel } from "@microsoft/signalr";
import { message, notification } from "antd";
import { useAuthCookiePersistence } from "hooks/useAuthCookiePersistence";
import { createContext, FC, PropsWithChildren, useCallback, useContext, useEffect, useState } from "react";

export interface SignalR {
    connect: () => Promise<void>;
    disconnect: () => Promise<void>;
}

const SignalRContext = createContext<SignalR | null>(null);

export const SignalRProvider: FC<PropsWithChildren & {
    enabled: boolean
}> = ({ children, enabled }) => {
    const { authTokenFactory } = useAuthCookiePersistence();
    const [connection, setConnection] = useState<HubConnection>();

    const createHubConnection = useCallback(async () => {
        if (!enabled) {
            return;
        }

        const conn = new HubConnectionBuilder()
            .withUrl("/ws/devices", {
                accessTokenFactory: authTokenFactory
            })
            .configureLogging(LogLevel.None)
            .withAutomaticReconnect()
            .build();


        conn.on("emit", (...args: object[]) => {
            notification.info({
                message: "Debug Event",
                description: JSON.stringify(args, null, 2),
                placement: 'top',
                maxCount: 1,
                duration: 2
            });
        });

        conn.on("consignmentBalanceUpdated", () => {
            message.info("Your balance has been updated!");
        });

        conn.on("submissionReceived", (friendlyPackageId: number) => {
            message.info(`We received your package! (#${friendlyPackageId})`);
        });

        conn.on("supportTicketReplied", (ticketId: number) => {
            message.info(`Your ticket #${ticketId} has been replied to!`);
        });

        setConnection(conn);
    }, [authTokenFactory, enabled]);

    const connect = useCallback(async () => {
        try {
            if (connection?.state === HubConnectionState.Disconnected) {
                await connection.start();
            }
        } catch (err) {
            console.error(err);
        }
    }, [connection]);

    const disconnect = useCallback(async () => {
        if (connection?.state === HubConnectionState.Connected) {
            await connection.stop();
        }
    }, [connection]);

    useEffect(() => {
        createHubConnection();
    }, [createHubConnection]);

    return <SignalRContext.Provider value={{
        connect,
        disconnect
    }}>
        {children}
    </SignalRContext.Provider>;
}

export function useSignalR() {
    const context = useContext(SignalRContext)
    if (!context) {
        throw new Error("useSignalR must be used within an SignalRProvider")
    }
    return context
}